import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import * as XLSX from 'xlsx'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { orderBy, filterBy } from '@progress/kendo-data-query'
import { Button } from '@progress/kendo-react-buttons'
import { Checkbox } from '@progress/kendo-react-inputs'
import { FaFileExcel, FaSyncAlt } from 'react-icons/fa'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from 'recharts'
import { getCurrentUserEmployee } from '../../data/employeeData'
import { appendEmployeeIds } from '../../data/reportUserScopeData'
import ReportUserFilter, { useReportUserFilter } from '../../components/ReportUserFilter/ReportUserFilter'
import { useToast } from '../../contexts/ToastContext'
import './EmployeeOpenNotesReport.css'

interface EmployeeOpenNotesRow {
  employeeId: number
  employeeName: string
  openNotes: number
  isLeaver: boolean
}

interface EmployeeOpenNotesReport {
  employees: EmployeeOpenNotesRow[]
  totalOpenNotes: number
  distinctOpenNotes: number
  employeesWithOpenNotes: number
}

const EMPTY_REPORT: EmployeeOpenNotesReport = {
  employees: [],
  totalOpenNotes: 0,
  distinctOpenNotes: 0,
  employeesWithOpenNotes: 0,
}

// Recessive ink for axes - deliberately not the series colour, so axis text
// never reads as an identity.
const axisTick = { fontSize: '9pt', fill: '#5a6472' }

// One series, so colour is not doing identity work here: it is the portal navy
// the other reports use for counts.
const COLOUR_COUNT = '#1e2a4e'

// How many people the chart shows before it stops being readable. The grid below
// carries everyone.
const CHART_ROWS = 12

// Names run long and collide with the plot on a category axis. The tooltip and
// the grid both still show the full name.
const truncateName = (value: string) =>
  typeof value === 'string' && value.length > 18 ? `${value.slice(0, 17)}…` : value

function EmployeeOpenNotesReport() {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [report, setReport] = useState<EmployeeOpenNotesReport>(EMPTY_REPORT)
  // On by default: the report is read to find outstanding work, and a page of
  // zeroes buries it. The API still returns everyone, so unticking this shows
  // the whole team without another round trip.
  const [onlyWithNotes, setOnlyWithNotes] = useState(true)
  const [loading, setLoading] = useState(false)
  const [hasPermission, setHasPermission] = useState(false)
  const users = useReportUserFilter(hasPermission)

  // Alphabetical, so a name can be found without scanning. The column headers
  // still re-sort, and the chart below stays ranked by count.
  const [sort, setSort] = useState<any[]>([{ field: 'employeeName', dir: 'asc' }])
  const [filter, setFilter] = useState<any>(null)
  const [skip, setSkip] = useState(0)
  const [pageSize] = useState(25)

  const apiURL = process.env.REACT_APP_API_URL

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const currentUser = await getCurrentUserEmployee()
        if (currentUser && currentUser.permissions) {
          // An administrator gets in without the report role being assigned,
          // matching the policy the API endpoint is trimmed with.
          const permitted = currentUser.permissions.some(
            (p) =>
              p.assigned &&
              ((p.area === 'Portal' && p.permission === 'Administrator') ||
                (p.area === 'Reports' && p.permission === 'EmployeeOpenNotes'))
          )
          setHasPermission(permitted)
          if (!permitted) {
            showToast('You do not have permission to access this page', 'error')
            navigate('/')
          }
        } else {
          showToast('Unable to verify permissions', 'error')
          navigate('/')
        }
      } catch (error) {
        console.error('Error checking permissions:', error)
        showToast('Error verifying permissions', 'error')
        navigate('/')
      }
    }
    checkPermissions()
  }, [navigate, showToast])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('Authorization token not found')

      const query = appendEmployeeIds(new URLSearchParams(), users.selectedIds)

      const response = await axios.get<EmployeeOpenNotesReport>(
        `https://${apiURL}/Reports/EmployeeOpenNotes${query.toString() ? '?' + query : ''}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
          },
        }
      )
      setReport(response.data)
      setSkip(0)
      showToast(
        `${response.data.totalOpenNotes} open notes across ${response.data.employeesWithOpenNotes} employees`,
        'success'
      )
    } catch (error: any) {
      console.error('Error fetching employee open notes:', error)
      const msg = error.response?.data?.message || error.message || 'Error loading the report'
      showToast(msg, 'error')
    } finally {
      setLoading(false)
    }
  }, [users.selectedIds, showToast, apiURL])

  // Open notes are a snapshot rather than a range, so the only thing to pick is
  // who - the report loads as soon as the user filter has its default, and
  // again whenever the users change.
  useEffect(() => {
    if (hasPermission && users.ready) fetchData()
  }, [hasPermission, users.ready, fetchData])

  const rows = onlyWithNotes
    ? report.employees.filter((e) => e.openNotes > 0)
    : report.employees

  const rowsFiltered = filter ? filterBy(rows, filter) : rows
  const rowsSorted = orderBy(rowsFiltered, sort)

  // The total follows whatever the grid is showing, so it can never disagree
  // with the rows above it once a filter is applied.
  const shownTotal = rowsSorted.reduce((sum, row) => sum + row.openNotes, 0)

  // Ranked bars read top-down and Recharts draws a vertical layout bottom-up,
  // so the busiest has to go last.
  const chartData = [...report.employees]
    .filter((e) => e.openNotes > 0)
    .sort((a, b) => b.openNotes - a.openNotes)
    .slice(0, CHART_ROWS)
    .reverse()

  const exportToExcel = () => {
    const exportData = rowsSorted.map((row) => ({
      'Employee': row.employeeName,
      'Open Notes': row.openNotes,
      'Status': row.isLeaver ? 'Left' : 'Current',
    }))
    // The total travels with the sheet, so the export stands on its own rather
    // than needing the reader to re-add the column.
    exportData.push({ 'Employee': 'Total', 'Open Notes': shownTotal, 'Status': '' })

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(exportData),
      'Open Notes by Employee'
    )

    const fileName = `employee_open_notes_${new Date().toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
    showToast(`Exported ${rowsSorted.length} employees to ${fileName}`, 'success')
  }

  if (!hasPermission) return null

  return (
    <div>
      <div style={{ padding: '1rem' }}>
        <div className="employee-open-notes__header">
          <h1>Employee Open Notes</h1>
        </div>

        {/* Controls */}
        <div className="employee-open-notes__filters">
          <ReportUserFilter
            scope={users.scope}
            value={users.selectedIds}
            onChange={users.setSelectedIds}
            disabled={loading}
            label="Users"
            labelClassName="employee-open-notes__filter-label"
          />
          <div>
            <Button onClick={fetchData} disabled={loading} themeColor="primary">
              <FaSyncAlt style={{ marginRight: '0.4rem' }} />
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </div>
          <div>
            <Button onClick={exportToExcel} disabled={rowsSorted.length === 0} data-excel="true">
              <FaFileExcel style={{ marginRight: '0.4rem' }} />
              Export to Excel
            </Button>
          </div>
          <div className="employee-open-notes__toggle">
            <Checkbox
              value={onlyWithNotes}
              label="Only show employees with open notes"
              onChange={(e) => {
                setOnlyWithNotes(e.value)
                setSkip(0)
              }}
            />
          </div>
        </div>

        {/* Headline figures */}
        <div className="employee-open-notes__kpis">
          <div className="employee-open-notes__kpi employee-open-notes__kpi--navy">
            <div className="employee-open-notes__kpi-label">Open notes</div>
            <div className="employee-open-notes__kpi-value">{report.totalOpenNotes}</div>
            <div className="employee-open-notes__kpi-sub">Added up across employees</div>
          </div>
          <div className="employee-open-notes__kpi employee-open-notes__kpi--green">
            <div className="employee-open-notes__kpi-label">Notes outstanding</div>
            <div className="employee-open-notes__kpi-value">{report.distinctOpenNotes}</div>
            <div className="employee-open-notes__kpi-sub">
              Lower where a note has more than one assignee
            </div>
          </div>
          <div className="employee-open-notes__kpi employee-open-notes__kpi--orange">
            <div className="employee-open-notes__kpi-label">Employees with open notes</div>
            <div className="employee-open-notes__kpi-value">{report.employeesWithOpenNotes}</div>
            <div className="employee-open-notes__kpi-sub">
              of {report.employees.length} in the report
            </div>
          </div>
        </div>

        {/* Busiest people, for the shape of the workload */}
        {chartData.length > 0 && (
          <div className="employee-open-notes__chart">
            <h3>
              {chartData.length < report.employeesWithOpenNotes
                ? `Most Open Notes (top ${chartData.length})`
                : 'Open Notes by Employee'}
            </h3>
            <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 30)}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 40, left: 20, bottom: 5 }}
              >
                <CartesianGrid stroke="#eef1f4" horizontal={false} />
                <XAxis type="number" tick={axisTick} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="employeeName"
                  tick={axisTick}
                  tickFormatter={truncateName}
                  width={130}
                />
                <Tooltip
                  formatter={(value: any) => [`${value} open notes`, '']}
                  labelFormatter={(label: any) => label}
                />
                <Bar dataKey="openNotes" fill={COLOUR_COUNT} radius={[0, 4, 4, 0]} barSize={16}>
                  <LabelList
                    dataKey="openNotes"
                    position="right"
                    style={{ fontSize: '9pt', fill: '#1e2a4e' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* The list, with the total beneath it */}
        <div className="employee-open-notes-grid">
          <Grid
            data={rowsSorted.slice(skip, skip + pageSize)}
            total={rowsSorted.length}
            skip={skip}
            pageSize={pageSize}
            pageable={{ buttonCount: 5, pageSizes: false }}
            onPageChange={(e) => setSkip(e.page.skip)}
            sortable
            sort={sort}
            onSortChange={(e) => setSort(e.sort)}
            filterable
            filter={filter}
            onFilterChange={(e) => setFilter(e.filter)}
          >
            <GridColumn
              field="employeeName"
              title="Employee"
              cell={(props) => (
                <td>
                  {props.dataItem.employeeName}
                  {props.dataItem.isLeaver && (
                    <span className="employee-open-notes__leaver">Left</span>
                  )}
                </td>
              )}
              footerCell={(props: any) => (
                <td
                  className="k-table-td employee-open-notes__footer"
                  colSpan={props.colSpan}
                  style={props.style}
                  role="gridcell"
                >
                  Total
                </td>
              )}
            />
            <GridColumn
              field="openNotes"
              title="Open Notes"
              filter="numeric"
              width="180px"
              cell={(props) => (
                <td className="employee-open-notes__num">{props.dataItem.openNotes}</td>
              )}
              footerCell={(props: any) => (
                <td
                  className="k-table-td employee-open-notes__footer employee-open-notes__num"
                  colSpan={props.colSpan}
                  style={props.style}
                  role="gridcell"
                >
                  {shownTotal}
                </td>
              )}
            />
          </Grid>
        </div>
      </div>
    </div>
  )
}

export default EmployeeOpenNotesReport
